import { Avatar, AvatarFallback } from '@journ/ui/avatar'
import { UserAvatar } from '@/components/user-avatar'
import type { WorkItemPriority } from '../domain/workflow'

// Linear-style priority indicator: a compact set of bars whose fill and color
// communicate urgency at a glance. P0/P1 read as hot (red/amber), the rest stay
// muted so the board doesn't turn into a wall of color.
const priorityMeta: Record<WorkItemPriority, { bars: number; color: string; label: string }> = {
	P0: { bars: 3, color: 'bg-red-500', label: 'P0' },
	P1: { bars: 3, color: 'bg-amber-500', label: 'P1' },
	P2: { bars: 2, color: 'bg-orange-400', label: 'P2' },
	P3: { bars: 2, color: 'bg-sky-400', label: 'P3' },
	P4: { bars: 1, color: 'bg-slate-400', label: 'P4' },
}

type PriorityIndicatorProps = {
	priority: WorkItemPriority
}

const PriorityIndicator = ({ priority }: PriorityIndicatorProps) => {
	const meta = priorityMeta[priority]
	return (
		<span className='flex items-end gap-0.5' title={meta.label} aria-label={meta.label}>
			{[0, 1, 2].map(i => (
				<span
					key={i}
					className={`w-1 rounded-[1px] ${i < meta.bars ? meta.color : 'bg-muted-foreground/15'}`}
					style={{ height: `${5 + i * 3}px` }}
				/>
			))}
		</span>
	)
}

type AssigneeAvatarProps = {
	name: string | null
	avatarUrl?: string | null
}

const AssigneeAvatar = ({ name, avatarUrl }: AssigneeAvatarProps) => {
	if (!name) {
		return (
			<Avatar
				className='size-5 border border-dashed border-muted-foreground/30 bg-transparent text-[9px] text-muted-foreground/50'
				title={undefined}
			>
				<AvatarFallback className='bg-transparent text-inherit'>?</AvatarFallback>
			</Avatar>
		)
	}
	return <UserAvatar name={name} avatarUrl={avatarUrl} size='xs' />
}

export { PriorityIndicator, AssigneeAvatar }
